// Home screen — rooms overview with hero + tiles + weather card + scene rail.
// Reads the shared room state and the BOM weather model (weather/). Static "current room" is
// derived from latest activity (lights on, sonos playing) with a manual fallback.
//
// Ported from design/new design/home.jsx. ESM imports replace window globals.

import * as React from 'react';
import {
  useClock, fmtTime, fmtDate, greeting,
} from './shared.jsx';
import { A_FONT, ALabel, IconBtn, fs, tvIsOn } from './direction-a.jsx';
import { RADIUS } from './theme.js';
import { NowPlayingStrip } from './music/index.jsx';
import { Degrees } from './degrees.jsx';
import { WeatherCard, OutsideReading } from './weather/index.jsx';
import { PowerCard, LaundryLine } from './power/index.jsx';
import { PlantsLine } from './plants/index.jsx';

const H_FONT = A_FONT; // share the Atelier type system
const H_LABEL = ALabel;

// Pick a "featured" room: prefer one with sonos playing, then most lights on, then first.
function pickFeatured(rooms) {
  const playing = rooms.find(r => r.sonos && r.sonos.playing);
  if (playing) return playing.id;
  const lit = [...rooms].sort((a, b) =>
    b.lights.filter(l => l.on).length - a.lights.filter(l => l.on).length);
  if (lit[0] && lit[0].lights.some(l => l.on)) return lit[0].id;
  return rooms[0].id;
}

// Room photo — uses the user-supplied imagery, with a warm vignette overlay
// for type contrast. In dark mode, prefer the paired night-mode photo if
// supplied; otherwise fall back to the day photo with a CSS filter that
// darkens + warms it to approximate evening light, plus a heavier vignette.
function RoomArt({ T, room, h = 160, w = '100%' }) {
  const useDayFallback = T.isDark && !room.photoNight;
  const photo = T.isDark ? (room.photoNight || room.photoDay) : room.photoDay;
  return (
    <div style={{
      width: w, height: h, overflow: 'hidden',
      background: T.cardSoft, position: 'relative',
    }}>
      {photo ? (
        <img src={photo} alt={room.name}
          style={{
            width: '100%', height: '100%', objectFit: 'cover', display: 'block',
            filter: useDayFallback
              ? 'brightness(0.45) saturate(0.85) hue-rotate(-8deg) contrast(1.05)'
              : 'saturate(.92)',
          }}/>
      ) : (
        <div style={{ width: '100%', height: '100%', background: T.cardSoft }}/>
      )}
      <div style={{
        position: 'absolute', inset: 0,
        background: T.isDark
          ? 'linear-gradient(to top, rgba(8,6,4,.65), rgba(8,6,4,.15) 45%, transparent 70%)'
          : `linear-gradient(to top, ${T.ink}88, ${T.ink}11 45%, transparent 70%)`,
        pointerEvents: 'none',
      }}/>
    </div>
  );
}

// One line of room state. Lights lead, with the oak glow when any are
// on, because that is what people look at the panel to find out; the
// rest is plain text. A quiet room says so once.
function RoomStatus({ T, room }) {
  const onCount = (room.lights || []).filter(l => l.on).length;
  const bits = [];
  if (room.sonos?.playing) bits.push('Sonos playing');
  if (tvIsOn(room.tv)) bits.push('Screen on');
  const text = { fontFamily: H_FONT.sans, fontSize: fs(T, 14), whiteSpace: 'nowrap' };
  if (onCount === 0 && bits.length === 0) {
    return <div style={{ ...text, color: T.inkSoft }}>All off</div>;
  }
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
      {onCount > 0 && (
        <span style={{ ...text, display: 'inline-flex', alignItems: 'center', gap: 8, color: T.ink, fontWeight: 500 }}>
          <span aria-hidden style={{ width: 8, height: 8, borderRadius: '50%', background: T.litFill, boxShadow: `0 0 8px 2px ${T.litSoft}` }}/>
          {onCount} {onCount === 1 ? 'light' : 'lights'} on
        </span>
      )}
      {bits.map((b) => <span key={b} style={{ ...text, color: T.inkSoft }}>{b}</span>)}
    </div>
  );
}

// Room card — used both for hero (large) and grid (small)
function RoomCard({ T, room, onClick, variant = 'small', viewport }) {
  const hero = variant === 'hero';
  const displayTemp = room.temp != null ? room.temp : (room.poolTemp != null ? room.poolTemp : null);

  return (
    <button onClick={onClick} style={{
      textAlign: 'left', padding: 0, border: 'none',
      background: T.card, borderRadius: RADIUS.card, boxShadow: T.cardShadow, cursor: 'pointer',
      display: 'flex', flexDirection: 'column', overflow: 'hidden',
      transition: 'transform .15s, box-shadow .15s',
      // Hero always spans the full row width. On phone the grid is 2-col,
      // so the hero must span 2 to fill — otherwise it shares the row
      // with a small card and the landscape photo crops awkwardly into
      // the narrow-tall slot (the "washed-out" look on iPhone).
      gridColumn: hero ? 'span 2' : 'span 1',
      gridRow: hero && viewport !== 'phone' ? 'span 2' : 'span 1',
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = 'translateY(-2px)';
      e.currentTarget.style.boxShadow = T.shadow;
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = 'none';
      e.currentTarget.style.boxShadow = T.cardShadow;
    }}>
      <RoomArt T={T} room={room} h={hero ? (viewport === 'phone' ? 200 : 340) : 142}/>
      <div style={{ padding: hero ? '20px 24px 22px' : '14px 20px 18px', display: 'flex', flexDirection: 'column', gap: 12, flex: 1, justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
          <div style={{
            fontFamily: H_FONT.serif, fontWeight: 500,
            fontSize: hero ? (viewport === 'phone' ? 32 : 40) : 28,
            color: T.ink, lineHeight: 1, letterSpacing: '-.01em',
          }}>{room.name}</div>
          {displayTemp != null && (
            <Degrees T={T} value={displayTemp} size={hero ? 32 : 26}
              color={room.poolTemp != null && room.temp == null ? T.pool : T.ink}/>
          )}
        </div>
        <RoomStatus T={T} room={room}/>
      </div>
    </button>
  );
}

// Global scenes rail
const GLOBAL_SCENES = [
  { id: 'morning',   name: 'Good Morning',   sub: 'Wake the house' },
  { id: 'work',      name: 'Workday',        sub: 'Office bright, music low' },
  { id: 'dinner',    name: 'Dinner',         sub: 'Pendants low, kitchen warm' },
  { id: 'film',      name: 'Film',           sub: 'Rumpus dim, screen on' },
  { id: 'goodnight', name: 'Goodnight',      sub: 'All off, bedroom soft' },
  { id: 'away',      name: 'Away',           sub: 'Arm and lights off' },
];

// The whole-home scenes are placeholders like the room ones: these
// buttons have never had an action, and the first was always drawn as
// running. They take the redesign's key shape but stay disabled and
// dimmed until scenes are wired to Home Assistant (its own TODO item).
function ScenesRail({ T, viewport }) {
  return (
    <div>
      <ALabel T={T} style={{ marginBottom: 12 }}>Whole-home scenes</ALabel>
      <div style={{
        display: 'grid',
        gridTemplateColumns: viewport === 'phone' ? 'repeat(2, minmax(0, 1fr))' : viewport === 'tablet' ? 'repeat(3, minmax(0, 1fr))' : 'repeat(6, minmax(0, 1fr))',
        gap: 12,
      }}>
        {GLOBAL_SCENES.map((sc) => (
          <button key={sc.id} disabled title="Scenes aren't wired up yet" style={{
            textAlign: 'left', padding: '14px 16px', minHeight: 84,
            border: 'none', borderRadius: RADIUS.control,
            background: T.keyFill, boxShadow: T.keyShadow, color: T.ink, opacity: .5,
            cursor: 'default', display: 'flex', flexDirection: 'column', gap: 4,
          }}>
            <span style={{ fontFamily: H_FONT.sans, fontSize: fs(T, 15), fontWeight: 600, lineHeight: 1.2 }}>{sc.name}</span>
            <span style={{ fontFamily: H_FONT.sans, fontSize: fs(T, 13), color: T.inkSoft, lineHeight: 1.3 }}>{sc.sub}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function HomeView({ T, rooms, onOpenRoom, onMenuClick, onOpenMusic, onOpenWeather, weather, onOpenPower, power, onOpenPlants, plants, viewport, hass }) {
  const now = useClock();
  const featuredId = pickFeatured(rooms);
  const featured = rooms.find(r => r.id === featuredId) || rooms[0];
  const others = rooms.filter(r => r.id !== featured.id);

  const padding = viewport === 'phone' ? '14px 18px 28px' : viewport === 'tablet' ? '20px 26px 28px' : '26px 36px 36px';

  const gridCols = viewport === 'phone' ? 'repeat(2, minmax(0, 1fr))'
                  : viewport === 'tablet' ? 'repeat(3, minmax(0, 1fr))'
                  : 'repeat(4, minmax(0, 1fr))';

  return (
    <div style={{
      width: '100%', minHeight: '100%',
      background: T.paper, color: T.ink,
      fontFamily: H_FONT.sans,
    }}>
      <main style={{ padding, display: 'flex', flexDirection: 'column', gap: viewport === 'phone' ? 20 : 28 }}>
        {/* Top bar — only hamburger (we're already home) */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <IconBtn T={T} onClick={onMenuClick} ariaLabel="Menu">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"><path d="M4 7h16M4 12h16M4 17h16"/></svg>
          </IconBtn>
          <div style={{ fontFamily: H_FONT.sans, fontSize: fs(T, 15), fontWeight: 500, color: T.inkSoft, fontVariantNumeric: 'tabular-nums' }}>
            {fmtTime(now)} · {fmtDate(now)}
          </div>
        </div>

        {/* House header — the name, and what a passer-by wants, as
            large numerals: lights on, inside and outside temperature,
            rooms playing. Outside is the BOM station reading, so the
            weather card below does not repeat it. No coloured full
            stop: it was ornament. */}
        {(() => {
          const lightsOn = rooms.reduce((a, r) => a + (r.lights || []).filter(l => l.on).length, 0);
          const tempReadings = rooms.map(r => r.temp).filter(t => typeof t === 'number');
          const avgTemp = tempReadings.length > 0
            ? tempReadings.reduce((a, t) => a + t, 0) / tempReadings.length
            : null;
          const playing = rooms.filter(r => r.sonos?.playing).length;
          const num = { fontFamily: H_FONT.serif, fontWeight: 500, fontSize: 32, lineHeight: 1, fontVariantNumeric: 'lining-nums tabular-nums', color: T.ink };
          const word = { fontFamily: H_FONT.sans, fontSize: fs(T, 14), color: T.inkSoft };
          return (
            <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', columnGap: 24, rowGap: 14 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <H_LABEL T={T}>{greeting(now)}</H_LABEL>
                <h1 style={{
                  fontFamily: H_FONT.serif, fontWeight: 300,
                  fontSize: viewport === 'phone' ? 52 : viewport === 'tablet' ? 64 : 80,
                  color: T.ink, margin: 0, lineHeight: .9, letterSpacing: '-.02em',
                }}>Wallace St</h1>
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: viewport === 'phone' ? 20 : 32, paddingBottom: 6, flexWrap: 'wrap' }}>
                <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 6 }}>
                  {lightsOn > 0 && <span aria-hidden style={{ width: 8, height: 8, borderRadius: '50%', alignSelf: 'center', background: T.litFill, boxShadow: `0 0 8px 2px ${T.litSoft}` }}/>}
                  <span style={num}>{lightsOn}</span>
                  <span style={word}>{lightsOn === 1 ? 'light on' : 'lights on'}</span>
                </span>
                {avgTemp != null && (
                  <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 6 }}>
                    <Degrees T={T} value={avgTemp} size={32}/>
                    <span style={word}>inside</span>
                  </span>
                )}
                <OutsideReading T={T} weather={weather} num={32} word={word}/>
                <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 6 }}>
                  <span style={num}>{playing}</span>
                  <span style={word}>{playing === 1 ? 'room playing' : 'rooms playing'}</span>
                </span>
              </div>
            </header>
          );
        })()}

        {/* A plant below its watering point; renders nothing otherwise. */}
        {plants && <PlantsLine T={T} plants={plants} onOpen={onOpenPlants}/>}

        {/* The washer or dryer held off by a price spike, with Run anyway;
            renders nothing otherwise (power/laundry.js). */}
        {power?.laundry && <LaundryLine T={T} laundry={power.laundry} hass={hass}/>}

        {/* Now playing (the primary entry into MusicShell; renders nothing
            when no room is playing) across the top, then the weather and
            power cards side by side, which open their pages. Until the
            power card (2026-10-22) now playing sat beside the weather. */}
        {(() => {
          const anyPlaying = rooms.some(r => r.sonos?.playing);
          const phone = viewport === 'phone';
          return (
            <div style={{ display: 'grid', gridTemplateColumns: phone ? 'minmax(0, 1fr)' : 'minmax(0, 1.25fr) minmax(0, 1fr)', gap: 20 }}>
              {anyPlaying && <div style={{ gridColumn: '1 / -1' }}><NowPlayingStrip T={T} rooms={rooms} onOpen={onOpenMusic}/></div>}
              <WeatherCard T={T} weather={weather} onOpen={onOpenWeather} compact/>
              {power && <PowerCard T={T} power={power} onOpen={onOpenPower} compact={phone}/>}
            </div>
          );
        })()}

        {/* Room grid */}
        <div>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 10 }}>
            <H_LABEL T={T}>Rooms</H_LABEL>
            <span style={{ fontFamily: H_FONT.sans, fontSize: fs(T, 13), color: T.inkSoft }}>{rooms.length} spaces</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: gridCols, gap: viewport === 'phone' ? 12 : 20, gridAutoRows: 'auto' }}>
            <RoomCard T={T} room={featured} variant="hero" viewport={viewport} onClick={() => onOpenRoom(featured.id)}/>
            {others.map(r => (
              <RoomCard key={r.id} T={T} room={r} variant="small" viewport={viewport} onClick={() => onOpenRoom(r.id)}/>
            ))}
          </div>
        </div>

        <ScenesRail T={T} viewport={viewport}/>
      </main>
    </div>
  );
}

export default HomeView;
export { pickFeatured };
